import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageWrapper from './PageWrapper';
import './BuylistCSVUploader.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const readFileAsText = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => resolve(e.target.result);
  reader.onerror = reject;
  reader.readAsText(file);
});

// ─── Component ────────────────────────────────────────────────────────────────
export default function BuylistCSVUploader() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verified, setVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [storeCredit, setStoreCredit] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = async (selectedFile) => {
    setLoading(true);
    setError(null);

    try {
      if (!selectedFile.name.toLowerCase().endsWith('.csv')) throw new Error('Please upload a CSV file');
      const fileContent = await readFileAsText(selectedFile);
      if (!fileContent || fileContent.trim() === '') throw new Error('The CSV file is empty');
      const lines = fileContent.split('\n').filter(l => l.trim() !== '');
      if (lines.length < 2) throw new Error('CSV must contain at least a header row and one data row');

      const response = await fetch('/api/upload-buylist-v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csvContent: fileContent, fileName: selectedFile.name }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process file');
      }

      const data = await response.json();
      setProcessedData({ ...data, fileName: selectedFile.name });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setProcessedData(null);
    setError(null);
    handleUpload(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (!f) return;
    setFile(f);
    setProcessedData(null);
    setError(null);
    handleUpload(f);
  };

  const handleStoreCredit = () => {
    if (!processedData) return;
    const newStoreCredit = !storeCredit;
    setStoreCredit(newStoreCredit);
    const cards = processedData.cards.map(card => {
      const basePrice = card.storeCredit ? card.pricePerCard / 1.2 : card.pricePerCard;
      const newPrice = newStoreCredit ? basePrice * 1.2 : basePrice;
      const newTotal = newPrice * card.quantity;
      return { ...card, pricePerCard: parseFloat(newPrice.toFixed(2)), total: parseFloat(newTotal.toFixed(2)), storeCredit: newStoreCredit };
    });
    let newTotalValue = 0;
    cards.forEach(card => { newTotalValue += card.total; });
    setProcessedData({ ...processedData, cards, totalValue: parseFloat(newTotalValue.toFixed(2)) });
  };

  const handleSubmitToSheets = async () => {
    if (!verified || !processedData) return;
    setSubmitting(true);
    setError(null);
    try {
      let user = null;
      try {
        user = JSON.parse(localStorage.getItem('user') || 'null');
      } catch {}

      const token = typeof user?.accessToken === 'string' ? user.accessToken.trim() : '';
      const looksSafe = token && !/[\r\n]/.test(token) && token.length > 20;

      const response = await fetch('/api/submit-buylist-to-sheets', {
        method: 'POST',
        headers: looksSafe
          ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
          : { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...processedData,
          uploadedBy: user?.name || user?.email || 'Staff Portal User',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401) {
          localStorage.removeItem('user');
          setTimeout(() => navigate('/login'), 3000);
        }
        throw new Error(errorData.details || errorData.error || 'Failed to submit to Google Sheets');
      }

      const result = await response.json();
      if (!result.success) throw new Error('Operation did not succeed: ' + (result.message || 'Unknown error'));

      setShowSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setError(err.message || 'Failed to submit data. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper
      title="Buylist Uploader"
      subtitle="Upload a Whatnot CSV export to calculate buylist prices and submit to Google Sheets."
      action={
        processedData && (
          <label className="rc-btn bl-file-button">
            ↑ Upload New File
            <input type="file" accept=".csv" hidden onChange={handleFileChange} />
          </label>
        )
      }
    >
      {error && <div className="rc-alert rc-alert--error bl-message">⚠️ {error}</div>}
      {showSuccess && <div className="rc-alert rc-alert--success bl-message">✅ Successfully submitted to Google Sheets! Redirecting…</div>}

      {/* Upload zone */}
      {!processedData && (
        <div
          className="bl-drop"
          data-drag={dragOver ? 'true' : undefined}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {loading ? (
            <div className="bl-drop__busy">Processing file…</div>
          ) : (
            <>
              <div className="bl-drop__icon">↑</div>
              <div className="bl-drop__title">
                {file ? file.name : 'Drop CSV here or click to upload'}
              </div>
              <div className="bl-drop__hint">Whatnot order export (.csv)</div>
              <label className="rc-btn rc-btn--primary bl-file-button">
                Choose File
                <input type="file" accept=".csv" hidden onChange={handleFileChange} />
              </label>
            </>
          )}
        </div>
      )}

      {/* Results */}
      {processedData && (
        <>
          {/* Summary */}
          <div className="rc-stats bl-stats">
            {[
              { label: 'Buylist ID', value: processedData.buylistId },
              { label: 'Total Cards', value: `${processedData.totalCards} cards` },
              { label: 'Total Value', value: `£${processedData.totalValue}` },
              { label: 'Avg Per Card', value: `£${(processedData.totalValue / processedData.totalCards).toFixed(2)}` },
            ].map((item) => (
              <div key={item.label} className="rc-stat">
                <div className="rc-stat__label">{item.label}</div>
                <div className="rc-stat__value">{item.value}</div>
              </div>
            ))}
          </div>

          {/* Store credit toggle */}
          <div className="bl-credit">
            <label className="bl-credit__label">
              <div
                onClick={handleStoreCredit}
                className="bl-switch"
                data-on={storeCredit ? 'true' : 'false'}
              >
                <div className="bl-switch__knob" />
              </div>
              <span>Store Credit (+20%)</span>
            </label>
          </div>

          {/* Card table */}
          <div className="rc-panel bl-cards">
            <div className="bl-cards__title">Card Details</div>
            <div className="rc-table-wrap bl-cards__scroll">
              <table className="rc-table bl-table">
                <thead>
                  <tr>
                    <th>Card Name</th>
                    <th className="rc-num">Qty</th>
                    <th className="rc-num">Price Each</th>
                    <th className="rc-num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {processedData.cards.map((card, i) => (
                    <tr key={i}>
                      <td>{card.name}</td>
                      <td className="rc-num">{card.quantity}</td>
                      <td className="rc-num">£{card.pricePerCard.toFixed(2)}</td>
                      <td className="rc-num">£{card.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Submit */}
          <div className="bl-submit">
            <label className="bl-submit__check">
              <input
                type="checkbox"
                checked={verified}
                onChange={(e) => setVerified(e.target.checked)}
              />
              I have verified the data is correct
            </label>
            <button
              onClick={handleSubmitToSheets}
              disabled={!verified || submitting}
              className="rc-btn rc-btn--primary bl-submit__button"
            >
              {submitting ? 'Submitting…' : '✓ Submit to Google Sheets'}
            </button>
          </div>
        </>
      )}
    </PageWrapper>
  );
}
